import { Injectable } from '@angular/core';

import { filter, map } from 'rxjs';

import {
  CamBaseService,
  GraphEndpoint,
  GraphReponsePaged,
  HandleSimpleRequest,
  graphAggregatorEndpoint,
} from '@camelot/server';
import { isNonNullable } from '@camelot/utils';

import {
  PurchaseOrder,
  PurchaseOrderAmendmentCreationPayload,
  PurchaseOrderCreationPayload,
  PurchaseOrderModifierPayload,
} from './dto/purchase-order';
import { PurchaseOrderProfile } from './dto/purchase-order-profile';
import {
  CANCEL_PURCHASE_ORDER,
  CREATE_PURCHASE_ORDER,
  CREATE_PURCHASE_ORDER_AMENDMENT,
  UPDATE_PURCHASE_ORDER,
  VALIDATE_PURCHASE_ORDER,
} from './mutations';
import { GET_PURCHASE_ORDER, GET_PURCHASE_ORDER_PROFILES } from './queries';

const graphEndpoint: GraphEndpoint = {
  clientName: 'purchaseService',
  endpoint: 'purchase',
};

const CLEAR_CACHE = ['purchaseOrders'];

@Injectable({
  providedIn: 'root',
})
export class CamPurchaseOrdersService extends CamBaseService {
  public purchaseOrder = new HandleSimpleRequest<PurchaseOrder>();
  public profiles = new HandleSimpleRequest<PurchaseOrderProfile[]>();

  constructor() {
    super();
    super.registerRoutes({ graphEndpoint });
  }

  public fetchPurchaseOrder$(id: string) {
    return this.purchaseOrder.fetch(
      this._graphService
        .fetchQueryBuilder<GraphReponsePaged<PurchaseOrder>>(GET_PURCHASE_ORDER(id), graphEndpoint.clientName)
        .pipe(
          filter(isNonNullable),
          map(data => data.items?.[0] ?? null),
          filter(isNonNullable)
        )
    );
  }

  public fetchPurchaseOrderProfiles$(ids: string[]) {
    return this.profiles.fetch(
      this._graphService
        .fetchQueryBuilder<GraphReponsePaged<PurchaseOrderProfile>>(
          GET_PURCHASE_ORDER_PROFILES(ids),
          graphAggregatorEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  public createPurchaseOrder$(payload: PurchaseOrderCreationPayload) {
    return this._graphService.mutateBuilder<string>(
      CREATE_PURCHASE_ORDER(payload),
      graphEndpoint.clientName,
      CLEAR_CACHE
    );
  }

  public updatePurchaseOrder$(payload: PurchaseOrderModifierPayload) {
    return this._graphService.mutateBuilder<string>(
      UPDATE_PURCHASE_ORDER(payload),
      graphEndpoint.clientName,
      CLEAR_CACHE
    );
  }

  public createAmendment$(payload: PurchaseOrderAmendmentCreationPayload) {
    return this._graphService.mutateBuilder<string>(
      CREATE_PURCHASE_ORDER_AMENDMENT(payload),
      graphEndpoint.clientName,
      CLEAR_CACHE
    );
  }

  public validatePurchaseOrder$(purchaseOrderId: string) {
    return this._graphService.mutateBuilder<string>(
      VALIDATE_PURCHASE_ORDER(purchaseOrderId),
      graphEndpoint.clientName,
      CLEAR_CACHE
    );
  }

  public cancelPurchaseOrder$(purchaseOrderId: string) {
    return this._graphService.mutateBuilder<string>(
      CANCEL_PURCHASE_ORDER(purchaseOrderId),
      graphEndpoint.clientName,
      CLEAR_CACHE
    );
  }
}
